import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { listNotifications, getUnreadCount, markRead, markAllRead } from '../api/notifications';

function BellIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

function parseUtcIso(iso) {
  // Backend now emits explicit-UTC ISO (with `Z`). Older rows may be naive —
  // append `Z` so JS doesn't misread them as the device's local time.
  const hasTz = /Z$|[+-]\d{2}:?\d{2}$/.test(iso);
  return new Date(hasTz ? iso : iso + 'Z');
}

function timeAgo(iso, t) {
  if (!iso) return '';
  const diff = Math.floor((Date.now() - parseUtcIso(iso).getTime()) / 1000);
  if (diff < 60) return t('time.justNow');
  if (diff < 3600) return t('time.minutesAgo', { n: Math.floor(diff / 60) });
  if (diff < 86400) return t('time.hoursAgo', { n: Math.floor(diff / 3600) });
  return t('time.daysAgo', { n: Math.floor(diff / 86400) });
}

export default function NotificationBell({ liveSignal }) {
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  const refreshUnread = useCallback(async () => {
    setUnread(await getUnreadCount());
  }, []);

  const refreshList = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await listNotifications({ limit: 10 }));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refreshUnread(); }, [refreshUnread]);

  useEffect(() => {
    if (liveSignal == null) return;
    refreshUnread();
    if (open) refreshList();
  }, [liveSignal, open, refreshUnread, refreshList]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const onToggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) await refreshList();
  };

  const onItemClick = async (n) => {
    if (!n.read_at) {
      setItems((curr) => curr.map((it) => (it.id === n.id ? { ...it, read_at: new Date().toISOString() } : it)));
      setUnread((u) => Math.max(0, u - 1));
      markRead(n.id);
    }
    setOpen(false);
    if (n.contact_id) navigate(`/conversations?contactId=${n.contact_id}`);
    else navigate('/conversations');
  };

  const onMarkAll = async () => {
    if (unread === 0) return;
    setItems((curr) => curr.map((it) => (it.read_at ? it : { ...it, read_at: new Date().toISOString() })));
    setUnread(0);
    await markAllRead();
  };

  const eventLabel = (eventType) => {
    if (eventType === 'conversation.escalated') return t('events.conversationEscalated');
    if (eventType === 'conversation.stalled') return t('events.conversationStalled');
    if (eventType === 'ticket.transferred') return t('events.ticketTransferred');
    if (eventType === 'agent_message.received') return t('events.agentMessageReceived');
    return eventType;
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        className={`header-icon-btn ${open ? 'header-icon-btn--active' : ''}`}
        onClick={onToggle}
        aria-label={t('bell.aria')}
        title={t('bell.aria')}
        style={{ position: 'relative' }}
      >
        <BellIcon />
        {unread > 0 && (
          <span
            style={{
              position: 'absolute',
              top: 2,
              right: 2,
              minWidth: 16,
              height: 16,
              padding: '0 4px',
              borderRadius: 8,
              background: '#ef4444',
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
            }}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          className="account-dropdown notification-dropdown"
          style={{ minWidth: 340, maxWidth: 380, padding: 0, right: 0, left: 'auto' }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 14px',
              borderBottom: '1px solid #e5e7eb',
            }}
          >
            <span style={{ fontWeight: 600, fontSize: 14 }}>{t('title')}</span>
            <button
              onClick={onMarkAll}
              disabled={unread === 0}
              style={{
                fontSize: 12,
                color: unread === 0 ? '#9ca3af' : '#10b981',
                background: 'none',
                border: 'none',
                cursor: unread === 0 ? 'default' : 'pointer',
                padding: 0,
              }}
            >
              {t('markAllRead')}
            </button>
          </div>
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {loading && items.length === 0 && (
              <div style={{ padding: 16, fontSize: 13, color: '#6b7280', textAlign: 'center' }}>
                {t('loading')}
              </div>
            )}
            {!loading && items.length === 0 && (
              <div style={{ padding: 24, fontSize: 13, color: '#6b7280', textAlign: 'center' }}>
                {t('empty')}
              </div>
            )}
            {items.map((n) => (
              <button
                key={n.id}
                onClick={() => onItemClick(n)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '10px 14px',
                  border: 'none',
                  background: n.read_at ? 'transparent' : '#f0fdf4',
                  borderBottom: '1px solid #f3f4f6',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 2 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
                    {eventLabel(n.event_type)}
                  </span>
                  <span style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                    {timeAgo(n.created_at, t)}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: '#111827', lineHeight: 1.35 }}>{n.message}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
